import { useEffect, useRef } from "react";
import { Kreuz, Liste, Ordner, Textmarker, Waage } from "./Symbole";

/*
  Der Werkzeugkreis unten rechts.

  Idee aus dem Aufbauspiel Anno: ein Knopf, der immer an derselben Stelle
  liegt und sich auffächert. Man muss ihn nicht suchen und nicht erst
  irgendwohin scrollen — die Hand liegt beim Halten des Tablets ohnehin dort.

  Zwei Abweichungen vom Vorbild, beide mit Absicht:

  1. **Kein reiner Symbolkranz.** Anno kann sich Bildzeichen leisten, weil man
     sie stundenlang benutzt. Hier sitzt jemand einmal im Monat sonntagabends
     zwanzig Minuten davor und ist womöglich über siebzig. Deshalb steht neben
     jedem Symbol das Wort. „Lesbarkeit vor Dichte" — Regel aus CLAUDE.md.
  2. **Nach oben aufgefächert, nicht im Kreis.** Ein echter Ring drängt Punkte
     an den Bildschirmrand oder unter den Daumen. Eine Säule nach oben bleibt
     vollständig sichtbar und wächst mit, wenn Werkzeuge dazukommen.

  Was noch nicht gebaut ist, ist abgeblendet und trägt das Wort „geplant".
  Ein Knopf, der fertig aussieht und nichts tut, kostet mehr Vertrauen als ein
  ehrlich leerer.
*/

export const WERKZEUGE = [
  {
    schluessel: "markieren",
    titel: "Markieren",
    symbol: Textmarker,
    fertig: true,
    modus: true,
    erklaerung:
      "Stellen im Text hervorheben. Antippen einer Markierung fragt später, wohin sie soll.",
  },
  {
    schluessel: "notizen",
    titel: "Freie Notiz",
    symbol: Liste,
    fertig: false,
    erklaerung:
      "Notiz zum ganzen Punkt, ohne Bezug zu einer Textstelle. Die Notizen an einer Textstelle gibt es schon — im Reiter „Notizen“.",
  },
  {
    schluessel: "vergleichen",
    titel: "Fassungen vergleichen",
    symbol: Waage,
    fertig: false,
    erklaerung: "Zwei Fassungen nebeneinander, mit hervorgehobenen Änderungen.",
  },
  {
    schluessel: "akte",
    titel: "Akte",
    symbol: Ordner,
    fertig: false,
    erklaerung: "Alles, was die Fraktion zu diesem Vorgang gesammelt hat, an einer Stelle.",
  },
];

export default function Werkzeugkreis({
  offen,
  aufOffen,
  aufWerkzeug,
  aktiverModus = null,
  versteckt = false,
  hoch = false,
}) {
  const kreisRef = useRef(null);

  /*
    Der Knopf trägt das Zeichen des laufenden Werkzeugs. So sieht man auch mit
    geschlossenem Fächer, dass gerade markiert oder zitiert wird — und wundert
    sich nicht, warum das Auswählen von Text plötzlich etwas auslöst.
  */
  const laufend = WERKZEUGE.find((w) => w.schluessel === aktiverModus);
  const Werkzeugsymbol = laufend ? laufend.symbol : Textmarker;

  /* Escape schließt — wie überall sonst auch. */
  useEffect(() => {
    if (!offen) return undefined;
    const beiTaste = (e) => {
      if (e.key === "Escape") {
        aufOffen(false);
        if (kreisRef.current) kreisRef.current.focus();
      }
    };
    window.addEventListener("keydown", beiTaste);
    return () => window.removeEventListener("keydown", beiTaste);
  }, [offen, aufOffen]);

  if (versteckt) return null;

  return (
    <>
      {/*
        Die Fläche hinter dem Fächer. Sie fängt den Tipp daneben ab, damit man
        zum Schließen nicht den kleinen Knopf treffen muss — auf einem Tablet
        ist Danebentippen der Normalfall, nicht der Fehler.
      */}
      {offen && (
        <button
          className="wkreis__hinterfeld"
          aria-label="Werkzeuge schließen"
          onClick={() => aufOffen(false)}
        />
      )}

      <div className={`wkreis ${offen ? "wkreis--offen" : ""} ${hoch ? "wkreis--hoch" : ""}`}>
        {offen && (
          <div className="wkreis__faecher" role="menu" aria-label="Werkzeuge">
            {WERKZEUGE.map((w) => (
              <button
                key={w.schluessel}
                className={`wkreis__werkzeug ${w.fertig ? "" : "wkreis__werkzeug--geplant"} ${
                  aktiverModus === w.schluessel ? "wkreis__werkzeug--an" : ""
                }`}
                /*
                  Werkzeuge, die an- und ausgehen, sind Schalter — deshalb
                  „menuitemcheckbox“. Ein Bildschirmleser sagt damit an, ob
                  Markieren gerade läuft; sonst bliebe der Zustand unsichtbar.
                */
                role={w.modus ? "menuitemcheckbox" : "menuitem"}
                aria-checked={w.modus ? aktiverModus === w.schluessel : undefined}
                disabled={!w.fertig}
                onClick={() => {
                  aufOffen(false);
                  if (aufWerkzeug) aufWerkzeug(w.schluessel);
                }}
              >
                <span className="wkreis__symbol">
                  <w.symbol groesse={20} />
                </span>
                <span className="wkreis__text">
                  <span className="wkreis__titel">
                    {w.titel}
                    {!w.fertig && <span className="wkreis__band">geplant</span>}
                    {aktiverModus === w.schluessel && <span className="wkreis__band">an</span>}
                  </span>
                  <span className="wkreis__erklaerung">{w.erklaerung}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          ref={kreisRef}
          className={`wkreis__knopf ${aktiverModus ? "wkreis__knopf--an" : ""}`}
          aria-expanded={offen}
          aria-haspopup="menu"
          aria-label={offen ? "Werkzeuge schließen" : "Werkzeuge öffnen"}
          onClick={() => aufOffen(!offen)}
        >
          {offen ? <Kreuz groesse={26} /> : <Werkzeugsymbol groesse={26} />}
        </button>
      </div>
    </>
  );
}
